import { DexTradeTemplate } from 'api/types/httpsTypes/dex-trade';
import { parseAmount } from '../../../components/chains/units';
import {
  chainDefaultBackupBribeInBaseAsset,
  chainDefaultBribeInBaseAsset,
  chainDefaultBuyAmountInBaseAsset,
  chainHasGasLimit,
  chainHasMemPool,
  chainHasTax,
  chainMaxBuyGasPriority,
  Chains,
} from 'api/types/httpsTypes/d-wallets';

const createMizarTemplates = (chain: Chains): DexTradeTemplate[] => {
  const maxPriorityFeePerGasWei = parseAmount(
    chainMaxBuyGasPriority(chain).toString(),
    chain,
    'gweiToWei'
  ).toString();

  const positionSnipe = {
    buyAmountInWei: chainDefaultBuyAmountInBaseAsset(chain).toString(),
    bribe:
      chain === Chains.Ethereum
        ? chainDefaultBribeInBaseAsset(chain).toString()
        : '0',
    gasLimit: chainHasGasLimit(chain) ? 400000 : undefined,
    blockOneRetry: true,
    backupBribe: chainDefaultBackupBribeInBaseAsset(chain).toString(),
    antiMev: false,
    chain,
    honeypotProtection: true,
    maxBuyTax: chainHasTax(chain) ? 0.4 : 1,
    maxLiquidityInUsd: 150000,
    maxMarketcapInUsd: 1000000,
    maxPriorityFeePerGasWei,
    maxSellTax: chainHasTax(chain) ? 0.5 : 1,
    maxSlippage: '0.25',
    minLiquidityInUsd: 5000,
    minMarketcapInUsd: 20000,
    vaultId: '',
    autoRetryEnabled: true,
    autoRetry: {
      buy: { active: false, nRetries: 0 },
      takeProfit: { active: true, nRetries: 1 },
      stopLoss: { active: false, nRetries: 0 },
    },
    takeProfits: [
      {
        amountToSellPercentage: '0.5',
        maxPriorityFeePerGasWei,
        bribe: '0',
        maxSlippage: '0.25',
        antiMev: false,
        retry: true,
        nRetries: 1,
        priceIncreasePercentage: '2.5',
      },
      {
        amountToSellPercentage: '0.5',
        maxPriorityFeePerGasWei,
        bribe: '0',
        maxSlippage: '0.25',
        retry: true,
        nRetries: 1,
        antiMev: false,
        priceIncreasePercentage: '9',
      },
    ],
    limitOrders: [],
    trailingStopLoss: null,
  };

  const positionSnipeWithoutMemPool = {
    buyAmountInWei: chainDefaultBuyAmountInBaseAsset(chain).toString(),
    gasLimit: chainHasGasLimit(chain) ? 400000 : undefined,
    antiMev: false,
    chain,
    honeypotProtection: true,
    maxBuyTax: chainHasTax(chain) ? 0.4 : 1,
    maxLiquidityInUsd: 150000,
    maxMarketcapInUsd: 1000000,
    maxPriorityFeePerGasWei,
    bribe: '0',
    maxSellTax: chainHasTax(chain) ? 0.5 : 1,
    maxSlippage: '0.25',
    minLiquidityInUsd: 5000,
    minMarketcapInUsd: 20000,
    vaultId: '',
    autoRetryEnabled: true,
    autoRetry: {
      buy: { active: false, nRetries: 0 },
      takeProfit: { active: true, nRetries: 1 },
      stopLoss: { active: false, nRetries: 0 },
    },
    takeProfits: [
      {
        amountToSellPercentage: '0.5',
        maxPriorityFeePerGasWei,
        bribe: '0',
        maxSlippage: '0.25',
        antiMev: false,
        retry: true,
        nRetries: 1,
        priceIncreasePercentage: '2.5',
      },
      {
        amountToSellPercentage: '0.5',
        maxPriorityFeePerGasWei,
        bribe: '0',
        maxSlippage: '0.25',
        antiMev: false,
        retry: true,
        nRetries: 1,
        priceIncreasePercentage: '9',
      },
    ],
    limitOrders: [],
    trailingStopLoss: null,
  };

  const positionTrade = {
    buyAmountInWei: chainDefaultBuyAmountInBaseAsset(chain).toString(),
    antiMev: false,
    chain,
    honeypotProtection: true,
    maxBuyTax: chainHasTax(chain) ? 0.05 : 1,
    gasLimit: chainHasGasLimit(chain) ? 400000 : undefined,
    maxLiquidityInUsd: Number.MAX_SAFE_INTEGER,
    maxMarketcapInUsd: Number.MAX_SAFE_INTEGER,
    autoRetryEnabled: true,
    autoRetry: {
      buy: { active: false, nRetries: 0 },
      takeProfit: { active: true, nRetries: 1 },
      stopLoss: { active: false, nRetries: 0 },
    },
    maxPriorityFeePerGasWei,
    bribe: '0',
    maxSellTax: chainHasTax(chain) ? 0.15 : 1,
    maxSlippage: '0.15',
    minLiquidityInUsd: 0,
    minMarketcapInUsd: 0,
    vaultId: '',
    takeProfits: [
      {
        amountToSellPercentage: '0.5',
        maxPriorityFeePerGasWei,
        bribe: '0',
        maxSlippage: '0.15',
        retry: true,
        nRetries: 1,
        priceIncreasePercentage: '0.25',
        antiMev: false,
      },
      {
        amountToSellPercentage: '0.5',
        maxPriorityFeePerGasWei,
        bribe: '0',
        maxSlippage: '0.15',
        retry: true,
        nRetries: 1,
        priceIncreasePercentage: '0.5',
        antiMev: false,
      },
    ],
    limitOrders: [],
    trailingStopLoss: null,
  };

  const MZR_SNIPE: DexTradeTemplate = {
    id: 'mzr_sniper',
    name: 'MZR Sniper',
    isDefault: false,
    chain,
    wallets: [],
    isMizarTemplate: true,
    position: chainHasMemPool(chain)
      ? positionSnipe
      : positionSnipeWithoutMemPool,
  };

  const MZR_STANDARD: DexTradeTemplate = {
    id: 'mzr_trader',
    name: 'MZR Trader',
    isDefault: false,
    wallets: [],
    chain,
    isMizarTemplate: true,
    position: positionTrade,
  };

  return [MZR_STANDARD, MZR_SNIPE];
};

export const mizarTemplates = createMizarTemplates;
